import { Phone, MessageCircle, Camera } from "lucide-react";
import { BRAND, IMAGES, telLink, waLink } from "@/lib/brand";
import BrandStamp from "@/components/BrandStamp";
import usePageMeta from "@/hooks/usePageMeta";

const ITEMS = [
  {
    src: "https://static.prod-images.emergentagent.com/jobs/b35829a5-a4f8-4d7e-a662-70f82d3fda77/images/addbbd6f44d48a1aa37697cea7767becec79ea94ce8c288258a16cac2276cc0e.jpeg",
    title: "Bridal Necklace Sets",
    hindi: "दुल्हन के हार",
    tall: true,
  },
  {
    src: "https://static.prod-images.emergentagent.com/jobs/b35829a5-a4f8-4d7e-a662-70f82d3fda77/images/6ceece055bb1ad85b91ab5a81a76708d3de66b4a27271f06eee32a6c0b5694ef.jpeg",
    title: "Gold Coins & Biscuits",
    hindi: "सोने के सिक्के व बिस्किट",
  },
  {
    src: "https://static.prod-images.emergentagent.com/jobs/b35829a5-a4f8-4d7e-a662-70f82d3fda77/images/b1e3dbcc5e972ba1f61a7b1ea5abb3fbccdecde148508f3b8d933a661ace999d.jpeg",
    title: "Bangles & Kadas",
    hindi: "चूड़ियां व कड़े",
  },
  {
    src: "https://static.prod-images.emergentagent.com/jobs/b35829a5-a4f8-4d7e-a662-70f82d3fda77/images/f3a7f625df463685e3d43af309e924e3178d7a6ce1644a6a23353d1003fa0b5c.jpeg",
    title: "Silver — Coins, Bars & Payal",
    hindi: "चांदी",
    tall: true,
  },
  {
    src: "https://static.prod-images.emergentagent.com/jobs/b35829a5-a4f8-4d7e-a662-70f82d3fda77/images/9f31eb6159b297695156f0503f133f748ac2f5e5456e12f15769e1fb2afad4f0.jpeg",
    title: "Live XRF Karat Testing",
    hindi: "लाइव जांच",
  },
  {
    src: "https://static.prod-images.emergentagent.com/jobs/b35829a5-a4f8-4d7e-a662-70f82d3fda77/images/245cd0f65e1d30424a3c3de2ef0fb162436cbc07cd0875070c69f934fac8d3e8.jpeg",
    title: "Instant Cash Payout",
    hindi: "तुरंत नकद",
  },
  {
    src: IMAGES.biscuits,
    title: "Bullion & Scrap Gold",
    hindi: "बुलियन व पुराना सोना",
  },
  {
    src: IMAGES.ornament1,
    title: "Traditional Ornaments",
    hindi: "पारंपरिक आभूषण",
    tall: true,
  },
  {
    src: IMAGES.hero,
    title: "Family Heirloom Jewellery",
    hindi: "पुश्तैनी गहने",
  },
];

export default function GalleryPage() {
  usePageMeta({
    title: "Gallery — Gold, Silver, Coins & Biscuits We Buy | Top Rate Gurgaon",
    description:
      "See the jewellery, gold coins, biscuits, silver and ornaments Top Rate buys every day in Gurgaon — with live karat testing and instant cash payout. Call 8796-432-666.",
    path: "/gallery",
    keywords:
      "gold buyer gallery gurgaon, sell gold coins gurgaon, sell gold biscuits, sell silver payal, cash for gold photos",
  });

  return (
    <div>
      <section className="bg-[#2D181A] text-[#FFFDF8]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-28">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
            <Camera size={13} /> Gallery · गैलरी
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-4 leading-[1.02] max-w-4xl">
            Everything gold &amp; silver —{" "}
            <span className="italic text-[#D4AF37]">we buy it all</span>.
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#FFFDF8]/80 leading-relaxed max-w-3xl">
            Necklaces, bangles, coins, biscuits, silver payal, broken or scrap
            gold — every item is tested live in front of you and paid at the
            best rate, in cash or bank transfer.
          </p>
        </div>
      </section>

      <BrandStamp
        variant="gold"
        tagline={<>Jewellery · Coins · Biscuits · Silver · Scrap — Best Rate for Everything</>}
      />

      <section
        data-testid="gallery-grid"
        className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24"
      >
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
          {ITEMS.map((it, i) => (
            <div
              key={it.title}
              data-testid={`gallery-item-${i}`}
              className="break-inside-avoid mb-6 group relative overflow-hidden bg-white border border-[#E5DCC5] shadow-sm"
            >
              <img
                src={it.src}
                alt={`${it.title} — Top Rate buys these at best rate in Gurgaon`}
                loading="lazy"
                className={`w-full object-cover transition-transform duration-500 group-hover:scale-[1.03] ${
                  it.tall ? "h-96 md:h-[28rem]" : "h-64 md:h-72"
                }`}
              />
              <div className="p-5 border-t-2 border-[#D4AF37]">
                <div className="font-display text-lg font-bold text-[#2D181A]">
                  {it.title}
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#641C24] mt-0.5">
                  {it.hindi}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <BrandStamp
        variant="dark"
        tagline={<>Sona Do Paisa Lo · Live Testing · Cash in 30 Minutes</>}
      />

      <section className="bg-[#FAF6EC] tr-grain border-y border-[#E5DCC5]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-[1.05]">
            Have something similar?{" "}
            <span className="italic text-[#641C24]">Get today's rate now.</span>
          </h2>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={telLink}
              data-testid="gallery-cta-call"
              className="inline-flex items-center gap-2 bg-[#641C24] hover:bg-[#4A151A] text-[#FFFDF8] px-8 py-4 text-sm font-bold uppercase tracking-wider"
            >
              <Phone size={17} /> Call {BRAND.phone}
            </a>
            <a
              href={waLink(
                "Namaste! Maine aapki gallery dekhi. Mere paas bhi kuch jewellery hai — best rate bataye. (Top Rate)"
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="gallery-cta-whatsapp"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FBA57] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider"
            >
              <MessageCircle size={17} /> Send Photos on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
